import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { css } from '@emotion/react';
import {
  StoryGamePromotionImg,
  StoryGamePromotionImg2,
  StoryGamePromotionImg3,
} from 'public/assets';
import theme from '@/styles/theme';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import useTranslation from 'next-translate/useTranslation';

const StoryGamePromotion = ({
  setIsShowPromotionStorygame,
}: {
  setIsShowPromotionStorygame: (value: boolean) => void;
}) => {
  const { isPC } = useWindowWidth();
  const { t } = useTranslation();

  const images = [
    StoryGamePromotionImg,
    StoryGamePromotionImg2,
    StoryGamePromotionImg3,
  ];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const transitionToNextImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    const timer = setInterval(transitionToNextImage, 2000);
    return () => clearInterval(timer);
  }, [transitionToNextImage]);

  return (
    <div css={styles.mainContainer}>
      <div css={styles.imageContainer}>
        <Image
          key={currentImageIndex}
          css={styles.image}
          layout="fill"
          src={images[currentImageIndex]}
          alt={`ai_character_${currentImageIndex + 1}`}
          priority
          objectPosition={'bottom'}
        />
      </div>

      <div css={styles.bottomContainer}>
        {isPC && (
          <>
            <div css={createOverlayStyle(0, 20, 8, 8)} />
            <div css={createOverlayStyle(20, 40, 7, 7)} />
            <div css={createOverlayStyle(40, 60, 6, 6)} />
            <div css={createOverlayStyle(60, 80, 5, 5)} />
            <div css={createOverlayStyle(80, 100, 4, 4)} />
          </>
        )}
        <div css={styles.bottomContainer2}>
          <div css={styles.title}>
            {t('storygame_ai_onboarding_screen_title_my_ai_game_profile')}
          </div>
          <div css={styles.subtitle}>
            {t(
              'storygame_ai_onboarding_screen_description_let_upload_self_photo',
            )}
          </div>
          <div
            css={styles.button}
            onClick={() => setIsShowPromotionStorygame(false)}
          >
            <div css={styles.buttonText}>
              {t('common_screen_title_confirm')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const overlayBaseStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(8px);
`;

const createOverlayStyle = (
  startPercentage: number,
  endPercentage: number,
  startBlur: number,
  endBlur: number,
) => css`
  ${overlayBaseStyle}
  clip-path: polygon(
    ${startPercentage}% 0%,
    ${endPercentage}% 0%,
    ${endPercentage}% 100%,
    ${startPercentage}% 100%
  );
  backdrop-filter: blur(${startBlur}px);
`;

export const styles = {
  mainContainer: css`
    width: 100%;
    height: 100%;
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: end;
    z-index: 3;
    @media screen and (max-width: ${theme.devices.md}px) {
      flex-direction: column;

      align-items: center;
      padding-top: 72px;
    }
  `,
  imageContainer: css`
    position: relative;
    width: 100%;
    height: 85vh;
    @media screen and (max-width: ${theme.devices.md}px) {
      height: 100%;
    }
  `,
  image: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0;
    animation: fadeIn 0.3s ease-in-out forwards;

    @keyframes fadeIn {
      from {
        opacity: 0.8;
      }
      to {
        opacity: 1;
      }
    }
    @media screen and (max-width: ${theme.devices.md}px) {
      position: relative;
      object-fit: cover;
      bottom: 0;
      @media (aspect-ratio > 1/1.2) {
        object-fit: contain;
      }
    }
  `,
  bottomContainer: css`
    position: absolute;
    min-width: 512px;
    width: 32%;
    height: 260px;
    border-top: 2px solid rgba(255, 255, 255, 0.1);
    border-bottom: 2px solid rgba(255, 255, 255, 0.1);
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0) 3%,
      rgba(0, 0, 0, 0.75) 20%,
      rgba(0, 0, 0, 0.75) 80%,
      rgba(0, 0, 0, 0) 97%
    );
    // backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    padding: 36px 40px;

    bottom: 0px;
    flex-direction: column;

    @media screen and (max-width: ${theme.devices.md}px) {
      position: relative;
      margin: 0 16px calc(env(safe-area-inset-top) + 16px) 16px;
      width: calc(100% - 32px);
      min-width: 0px;
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.25);
      background: var(--blackA50, rgba(0, 0, 0, 0.5));
      padding: 32px 24px 24px 24px;
      height: auto;
    }
  `,
  bottomContainer2: css`
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    @media screen and (max-width: ${theme.devices.md}px) {
    }
  `,
  title: css`
    text-align: center;
    color: white;
    font-size: 36px;
    font-weight: 800;
    word-wrap: break-word;
    @media screen and (max-width: ${theme.devices.md}px) {
      font-size: 28px;
    }
  `,
  subtitle: css`
    text-align: center;
    color: rgba(255, 255, 255, 0.75);
    font-size: 15px;
    font-weight: 500;
    line-height: 22px;
    word-wrap: break-word;
    margin-top: 6px;
  `,
  button: css`
    width: 173px;
    height: 48px;
    padding: 12px 16px;
    background: #753bbd;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    margin-top: 20px;
    cursor: pointer;
    @media screen and (max-width: ${theme.devices.md}px) {
      width: 100%;
    }
  `,
  buttonText: css`
    text-align: center;
    color: white;
    font-size: 16px;
    font-weight: 400;
    word-wrap: break-word;
  `,
  loadingText: css`
    color: white;
    font-size: 24px;
    text-align: center;
  `,
  imageWrapper: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `,
};

export default StoryGamePromotion;
